import {
  booleanAttribute,
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  forwardRef, HostBinding,
  inject, input,
  OnInit,
  signal,
} from '@angular/core';
import { IconComponent } from '@shared/components';
import { I18NextString } from '@core/models';
import { I18NEXT_SERVICE, I18NextModule } from 'angular-i18next';
import { filter } from 'rxjs/operators';
import { isNil } from 'lodash';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { LanguageLocales } from '@locales';

type SocialLink = {
  url: string;
  icon: string;
};

type FooterLink = {
  url: string;
  title: I18NextString;
};

@Component({
  standalone: true,
  selector: 'cheelee-footer',
  templateUrl: './footer.component.html',
  styleUrl: './footer.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    I18NextModule,
    forwardRef(() => IconComponent),
  ],
})
export class  FooterComponent implements OnInit {
  @HostBinding('class')
  get classes(): Record<string, boolean> {
    return {
      'chl-footer--compact': this.isCompact(),
    };
  }

  private readonly destroyRef = inject(DestroyRef);

  private readonly i18nService = inject(I18NEXT_SERVICE);

  readonly isCompact = input(false, { transform: booleanAttribute });

  protected readonly currentLang = signal<LanguageLocales>(LanguageLocales.RU);

  protected readonly socialLinks: SocialLink[] = [
    { url: 'https://www.instagram.com/cheelee.global', icon: 'instagram-icon' },
    { url: 'https://discord.gg/cheeleeone', icon: 'discord-icon' },
    { url: 'https://twitter.com/Cheelee_Tweet', icon: 'twitter-x-icon' },
    { url: 'https://www.youtube.com/@Cheelee_Eng', icon: 'youtube-icon' },
    { url: 'https://t.me/CheeleeCommunity_EN', icon: 'telegram-icon' },
  ];

  protected readonly links = computed<FooterLink[]>(() => {
    const isEn = this.currentLang() === 'en-US';
    const fileLang = isEn ? 'en_US' : 'ru_RU';
    const pageLang = isEn ? 'en' : 'ru';

    return [
      { url: 'https://t.me/CheeleeSupportBot', title: 'common:support' },
      { url: `https://static.cheeleepay.com/files/${fileLang}/terms-of-use.pdf`, title: 'common:terms of use' },
      { url: `https://static.cheeleepay.com/files/${fileLang}/privacy-policy.pdf`, title: 'common:privacy policy' },
      { url: `https://cheelee.us/${pageLang}/details.html`, title: 'common:details' },
    ];
  });

  ngOnInit(): void {
    this.languageChangeListener();
  }

  protected getCopyRightYears(): string {
    const startYear = 2023;
    const currentYear = new Date().getFullYear();

    if (currentYear > startYear) {
      return `${startYear} – ${currentYear}`;
    }

    return `${startYear}`;
  }

  private languageChangeListener(): void {
    this.i18nService.events.languageChanged
      .pipe(filter(langCode => !isNil(langCode)))
      .pipe(filter(langCode => Object.values(LanguageLocales).includes(langCode as LanguageLocales)))
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(lang => this.currentLang.set(lang as LanguageLocales));
  }
}
